import React, { useState } from "react";
import { X, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CITIES, ALL_NEIGHBORHOODS, PROPERTY_TYPES, TRANSACTION_TYPES } from "../lib/constants";

const SearchRequestModal = ({ onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    whatsapp: user?.whatsapp || user?.phone || "",
    city: "N'Djamena",
    neighborhood: "",
    property_type: "",
    transaction_type: "",
    budget_max: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/search-requests", {
        ...form,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
      });
      toast.success("✅ Recherche déposée ! Notre équipe vous contactera sur WhatsApp dès qu'un bien correspond.", { duration: 7000 });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="search-request-modal" className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-black text-xl tracking-tighter flex items-center gap-2">
            <Search className="h-5 w-5 text-[#FF6B1A]" /> Déposer une recherche
          </h3>
          <button onClick={onClose} data-testid="search-request-close" className="p-1 hover:bg-neutral-100 rounded-full"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-neutral-500 mb-4">Décrivez le bien que vous cherchez : notre équipe vous contacte sur WhatsApp dès qu'un bien correspond.</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="imora-label">Votre nom *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="sr-name" className="imora-input" />
          </div>
          <div>
            <label className="imora-label">Numéro WhatsApp *</label>
            <input required value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+235 ..." data-testid="sr-whatsapp" className="imora-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="imora-label">Ville</label>
              <select value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="sr-city" className="imora-input">
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="imora-label">Quartier</label>
              <select value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} data-testid="sr-neighborhood" className="imora-input">
                <option value="">Tous</option>
                {ALL_NEIGHBORHOODS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="imora-label">Type de bien</label>
            <select value={form.property_type} onChange={(e) => set("property_type", e.target.value)} data-testid="sr-property-type" className="imora-input">
              <option value="">Tous</option>
              {Object.values(PROPERTY_TYPES).map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((it) => <option key={it.value} value={it.value}>{it.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="imora-label">Transaction</label>
              <select value={form.transaction_type} onChange={(e) => set("transaction_type", e.target.value)} data-testid="sr-transaction" className="imora-input">
                <option value="">Toutes</option>
                {TRANSACTION_TYPES.map((tr) => <option key={tr.value} value={tr.value}>{tr.label}</option>)}
              </select>
            </div>
            <div>
              <label className="imora-label">Budget max (XAF)</label>
              <input type="number" min="0" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} placeholder="Ex. 25000000" data-testid="sr-budget" className="imora-input" />
            </div>
          </div>
          <div>
            <label className="imora-label">Précisions (optionnel)</label>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Ex. : je cherche une maison avec cour, proche d'une école…" data-testid="sr-message" className="imora-input min-h-[70px] py-2" />
          </div>
          <button type="submit" disabled={loading} data-testid="sr-submit" className="imora-btn-primary w-full disabled:opacity-60">
            <Send className="h-4 w-4" /> {loading ? "Envoi…" : "Envoyer ma recherche"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SearchRequestModal;
